import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { TemplateBlock } from "@/components/content/TemplateBlock";
import { Checklist } from "@/components/content/Checklist";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Search, DollarSign, HelpCircle, Phone } from "lucide-react";

const faqs = [
  {
    question: "How many service pages should I have?",
    answer: "Create a dedicated page for each service you want to rank for. Common lawn care pages: mowing, fertilization, weed control, aeration, overseeding, spring/fall cleanup, shrub trimming."
  },
  {
    question: "How long should each service page be?",
    answer: "Aim for 600-1200 words. Cover what the service includes, who it's for, your process, pricing info, FAQs, and a clear call-to-action."
  },
  {
    question: "Should I put pricing on service pages?",
    answer: "Yes! At minimum, include starting prices or price ranges. This pre-qualifies leads and builds trust. 'Lawn mowing starting at $35' is better than no pricing."
  },
  {
    question: "What's the best URL structure for service pages?",
    answer: "Use clean URLs like /lawn-mowing/, /fertilization/, /aeration/. For location-specific pages: /lawn-mowing-[city]/ or /[city]/lawn-mowing/."
  }
];

const LawnCareServicePageTemplate = () => {
  const pageUrl = "https://www.leafrushmarketing.com/website-templates/lawn-care-service-page-template/";
  
  return (
    <>
      <SEO
        title="Lawn Care Service Page Template | SEO-Optimized Structure"
        description="Free lawn care service page template. Create pages that rank for 'lawn mowing near me' and convert visitors into customers."
        url={pageUrl}
        keywords="lawn care service page template, lawn mowing page template, lawn care website pages"
      />
      <PageSchema
        pageUrl={pageUrl}
        pageName="Lawn Care Service Page Template"
        pageDescription="SEO-optimized service page template for lawn care websites."
        faqs={faqs}
      />

      <TopCTA />

      <section className="py-16 md:py-24">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge variant="secondary">Website Template</Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Lawn Care{" "}
              <span className="text-primary">Service Page Template</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Create service pages that rank for "[service] near me" searches 
              and convert visitors into paying customers.
            </p>
            <LastUpdated />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="text-2xl font-bold">Service Page Structure</h2>
            
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="h-5 w-5 text-primary" />
                    Section 1: SEO-Optimized Header
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="H1 Headline Template"
                    content={`[Service Name] in [City] – Professional [Service] Services

Examples:
• Lawn Mowing in Austin – Weekly & Bi-Weekly Mowing Services
• Fertilization & Weed Control in [City] – Healthier Lawns Guaranteed
• Aeration & Overseeding in [City] – Revive Your Lawn This Season`}
                  />
                  <TemplateBlock
                    title="Opening Paragraph"
                    content={`Looking for reliable [service] in [City]? [Company Name] provides professional [service] for residential lawns throughout [County]. Our [service] keeps your lawn healthy, green, and looking its best year-round.`}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    Section 2: What's Included
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="What's Included Section"
                    content={`What's Included in Our [Service]?

• [Specific task 1]
• [Specific task 2]
• [Specific task 3]
• [Specific task 4]

Every [service] visit includes [key benefit]. We leave your property clean and your lawn looking great.`}
                  />
                  <p className="text-muted-foreground text-sm">
                    Example for Lawn Mowing: "Every mowing visit includes edging along sidewalks and driveways, 
                    trimming around obstacles, and blowing off all hard surfaces."
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-primary" />
                    Section 3: Pricing Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Pricing Section Template"
                    content={`[Service] Pricing

Our [service] pricing is based on lawn size and frequency:

• Small lawns (under 5,000 sq ft): Starting at $[X]
• Medium lawns (5,000-10,000 sq ft): Starting at $[X]
• Large lawns (10,000+ sq ft): Custom quote

Weekly customers save [X]% compared to bi-weekly service.

[Get Your Custom Quote Button]`}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="h-5 w-5 text-primary" />
                    Section 4: FAQs
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="FAQ Section"
                    content={`Frequently Asked Questions About [Service]

Q: How often should I get [service]?
A: [Answer specific to the service]

Q: What's the best time of year for [service]?
A: [Seasonal answer]

Q: Do you offer [service] packages?
A: [Package/bundling information]

Q: What areas do you serve for [service]?
A: We provide [service] throughout [City], [City 2], and [County].`}
                  />
                  <p className="text-muted-foreground text-sm">
                    Include 4-6 FAQs specific to the service. These help with SEO and answer 
                    questions before visitors call.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-primary" />
                    Section 5: Call-to-Action
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Bottom CTA"
                    content={`Get Professional [Service] in [City]

Ready for reliable [service]? Get your free quote today. We'll provide transparent pricing and can often start within [timeframe].

[Get Free Quote Button] [Call (XXX) XXX-XXXX Button]`}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <MidPageCTA />

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">DIY vs. Done-For-You</h2>
            <DIYvsDoneForYou
              diyPoints={[
                "Create service pages using this template",
                "Write unique content for each service",
                "Optimize for local SEO keywords",
                "Time investment: 2-4 hours per page"
              ]}
              doneForYouPoints={[
                "We create all your service pages",
                "Professional copywriting included",
                "SEO-optimized from launch",
                "All pages included in subscription"
              ]}
            />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <InternalLinks
              title="Related Templates"
              links={[
                { to: "/website-templates/lawn-care-homepage-template/", label: "Lawn Care Homepage Template" },
                { to: "/website-templates/service-area-page-template/", label: "Service Area Page Template" },
                { to: "/website-templates/lawn-care-website-seo-checklist/", label: "Lawn Care SEO Checklist" },
                { to: "/service-pages-that-rank/", label: "Service Pages That Rank" },
                { to: "/website-templates/", label: "All Templates" }
              ]}
            />
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <BottomCTA />
      <CalendlyEmbed />
    </>
  );
};

export default LawnCareServicePageTemplate;
